import axios from 'axios';
import { shouldRedirectToLoginOn401 } from './apiAuthUtils';
import { getApiErrorMessage } from './utils/apiErrorMessages';
import { createConcurrencyGate, isGateExemptRequest } from './utils/concurrencyGate';
import { isSessionLocked, SessionChangedError } from './utils/sessionLock';
import { dispatchForceLogout, shouldSuppress401Logout } from './utils/authSession';

// API Logging: Only enabled in development mode
// Set to true temporarily if you need to debug production issues
const FORCE_API_LOGS = false;

// Determinar la URL base del backend
const getBackendURL = () => {
  const hostname = window.location.hostname;
  
  // 1. Si hay variable de entorno, usarla
  if (process.env.REACT_APP_API_URL) {
    return process.env.REACT_APP_API_URL;
  }
  
  // 2. Si estamos en producción (dominio archonpm.com), usar backend de producción
  if (hostname.includes('archonpm.com')) {
    return 'https://api.archonpm.com/api/v1';
  }
  
  // 3. Si estamos en Render, usar backend de Render
  if (hostname.includes('render.com')) {
    return 'https://axiom-m0hi.onrender.com/api/v1';
  }
  
  // 4. Desarrollo local
  return 'http://localhost:8000/api/v1';
};

// Get the base URL for uploads (without /api/v1)
const getUploadsBaseURL = () => {
  const hostname = window.location.hostname;
  
  if (process.env.REACT_APP_API_URL) {
    return process.env.REACT_APP_API_URL.replace('/api/v1', '');
  }
  
  if (hostname.includes('archonpm.com')) {
    return 'https://api.archonpm.com';
  }
  
  if (hostname.includes('render.com')) {
    return 'https://axiom-m0hi.onrender.com';
  }
  
  return 'http://localhost:8000';
};

/**
 * The uploads tree is not public.
 *
 * It holds candidate passports, ID scans, personnel photos, CVs and awarded
 * tender PDFs, and the server now refuses to serve any of it without proof of a
 * session. Most of it is rendered by `<img src=…>`, and an image request cannot
 * carry an `Authorization` header — so the SPA holds a short-lived ticket and
 * appends it to the URL.
 *
 * The ticket is not the session token. It is scoped to reading files, so the
 * string that ends up in browser history and `Referer` headers cannot be
 * replayed against the rest of the API, and it expires within the hour.
 *
 * Kept in module state *and* localStorage so that a page reload paints images
 * immediately instead of waiting for a round trip.
 */
const UPLOAD_TICKET_KEY = 'archon_upload_ticket';

let uploadTicket = (() => {
  try {
    const raw = localStorage.getItem(UPLOAD_TICKET_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
})();

let uploadTicketRefresh = null;

const ticketIsUsable = () =>
  !!uploadTicket?.ticket
  && !!uploadTicket.expires_at
  && new Date(uploadTicket.expires_at).getTime() > Date.now() + 60_000;

/**
 * Fetch a ticket and keep it fresh. Called once the session exists.
 * Safe to call repeatedly — it no-ops while the current ticket is still good.
 */
export const primeUploadTicket = async ({ force = false } = {}) => {
  if (!force && ticketIsUsable()) return uploadTicket;
  if (!localStorage.getItem('token')) return null;
  try {
    const { data } = await api.get('/uploads/ticket');
    uploadTicket = data;
    localStorage.setItem(UPLOAD_TICKET_KEY, JSON.stringify(data));
    // Renew a minute early: an image that 404s because the ticket died mid-view
    // looks like a missing file, which is the confusing failure to avoid.
    if (uploadTicketRefresh) clearTimeout(uploadTicketRefresh);
    const delay = Math.max(30_000, (data.ttl_seconds - 60) * 1000);
    uploadTicketRefresh = setTimeout(() => { primeUploadTicket({ force: true }); }, delay);
    return data;
  } catch {
    return null;
  }
};

export const clearUploadTicket = () => {
  uploadTicket = null;
  if (uploadTicketRefresh) clearTimeout(uploadTicketRefresh);
  uploadTicketRefresh = null;
  try { localStorage.removeItem(UPLOAD_TICKET_KEY); } catch { /* private mode */ }
};

/** Append the read ticket to a URL that a browser will fetch without headers. */
/**
 * Añade el ticket a una URL de uploads — sólo si el ticket SIRVE.
 *
 * Antes comprobaba `uploadTicket?.ticket` y nada más, teniendo `ticketIsUsable()`
 * escrita tres líneas más arriba. Un ticket caducado leído de `localStorage` se
 * adjuntaba igual, el backend lo rechazaba, y salía **404 sobre un fichero que
 * está**. Y como la puerta estática no tiene respaldo, ese 404 es
 * indistinguible de «el fichero no existe».
 *
 * Sin ticket usable se devuelve la URL **sin** parámetro: el 404 es el mismo,
 * pero deja de haber una credencial muerta viajando en la URL.
 */
const withUploadTicket = (url) => {
  if (!url || !ticketIsUsable()) return url;
  const sep = url.includes('?') ? '&' : '?';
  return `${url}${sep}t=${encodeURIComponent(uploadTicket.ticket)}`;
};

/**
 * Build full URL for uploaded files (photos, documents, etc.)
 * Handles both relative paths (/uploads/...) and already-full URLs
 * @param {string} path - The path or URL to the uploaded file
 * @returns {string|null} - The full URL or null if no path provided
 */
export const getUploadUrl = (path) => {
  if (!path) return null;

  // If already a full URL, return as-is
  if (path.startsWith('http://') || path.startsWith('https://')) {
    return path;
  }

  // Build full URL from relative path
  const baseUrl = getUploadsBaseURL();
  return withUploadTicket(`${baseUrl}${path}`);
};

/**
 * URL suitable for opening candidate uploads in a new tab (uses serve-upload API).
 */
export const getViewableDocumentUrl = (urlOrPath) => {
  if (!urlOrPath) return null;
  if (urlOrPath.startsWith('data:') || urlOrPath.startsWith('blob:')) {
    return urlOrPath;
  }
  const full = urlOrPath.startsWith('http://') || urlOrPath.startsWith('https://')
    ? urlOrPath
    : getUploadUrl(urlOrPath);
  if (!full) return null;
  const uploadsMatch = full.match(/\/uploads\/(.+)$/);
  if (uploadsMatch) {
    return withUploadTicket(`${getBackendURL()}/serve-upload/${uploadsMatch[1]}`);
  }
  return full;
};

/**
 * Open a candidate document in a new browser tab (images, PDFs, data/blob previews).
 */
export const openCandidateDocumentUrl = (urlOrPath) => {
  const url = getViewableDocumentUrl(urlOrPath);
  if (!url) return;

  if (url.startsWith('data:') || url.startsWith('blob:')) {
    const popup = window.open('', '_blank', 'noopener,noreferrer');
    if (!popup) return;
    const lower = url.toLowerCase();
    const isPdf = lower.startsWith('data:application/pdf') || lower.includes('application/pdf');
    if (isPdf) {
      popup.document.write(
        '<!DOCTYPE html><html><head><meta charset="utf-8"><title>Document</title></head>'
        + '<body style="margin:0;height:100vh;">'
        + `<embed src="${url}" type="application/pdf" width="100%" height="100%" />`
        + '</body></html>',
      );
    } else {
      popup.document.write(
        '<!DOCTYPE html><html><head><meta charset="utf-8"><title>Document</title></head>'
        + '<body style="margin:0;background:#111;display:flex;align-items:center;justify-content:center;min-height:100vh;">'
        + `<img src="${url}" alt="Document" style="max-width:100%;max-height:100vh;object-fit:contain;" />`
        + '</body></html>',
      );
    }
    popup.document.close();
    return;
  }

  window.open(url, '_blank', 'noopener,noreferrer');
};

/**
 * One-off query param so fetch() does not reuse a stale browser cache entry when the same
 * path is overwritten (e.g. company logo re-upload keeps /api/v1/company/logo/1_logo.jpg).
 * @param {string|null|undefined} url
 * @returns {string}
 */
export const withCacheBust = (url) => {
  if (!url) return '';
  const sep = url.includes('?') ? '&' : '?';
  return `${url}${sep}_=${Date.now()}`;
};

/**
 * URL for fetching an image (e.g. logos) for PDF/print. Use with fetch() in WeeklySummaryModal, etc.
 * In development (localhost:3000) uses relative path so setupProxy.js forwards /api -> backend (no CORS).
 * In production or when backend is different origin, returns full URL (backend must send CORS).
 */
export const getImageFetchUrl = (path) => {
  if (!path) return null;
  if (path.startsWith('http://') || path.startsWith('https://')) return path;
  const base = getBackendURL();
  const pathForApi = path.startsWith('/uploads/')
    ? `/api/v1/serve-upload/${path.replace(/^\/uploads\//, '')}`
    : (path.startsWith('/') ? path : `/${path}`);
  const fullUrl = path.startsWith('/uploads/')
    ? `${base}/serve-upload/${path.replace(/^\/uploads\//, '')}`
    : `${path.startsWith('/') ? getUploadsBaseURL() : base}${path.startsWith('/') ? path : `/${path}`}`;
  const isDevCra =
    typeof window !== 'undefined' &&
    String(window.location.port) === '3000' &&
    (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1');
  if (isDevCra) {
    return withUploadTicket(pathForApi);
  }
  return withUploadTicket(fullUrl);
};

// Default HTTP timeout (ms). Short values fail on cold starts (e.g. Render waking API + DB after idle).
// Override at build time: REACT_APP_API_TIMEOUT_MS=45000
const API_TIMEOUT_MS =
  Number(process.env.REACT_APP_API_TIMEOUT_MS) > 0
    ? Number(process.env.REACT_APP_API_TIMEOUT_MS)
    : 60000;

// Instancia central de axios
const api = axios.create({
  baseURL: getBackendURL(),
  timeout: API_TIMEOUT_MS,
});

// --- Client-side concurrency gate (Phase 1 / R37) ---
// Caps in-flight tenant GETs per tab so multi-window use stays under the DB pool.
// LOGIN-SAFE: /status, /token, /users/me bypass the gate entirely (see
// isGateExemptRequest + concurrencyGate.test.js). AuthContext, LoginForm, and
// App.checkDbStatus are unchanged — only axios scheduling differs for data routes.
const MAX_CONCURRENT_REQUESTS =
  Number(process.env.REACT_APP_MAX_CONCURRENT_REQUESTS) > 0
    ? Number(process.env.REACT_APP_MAX_CONCURRENT_REQUESTS)
    : 6;
const GATE_MAX_WAIT_MS = 20_000;

const requestGate = createConcurrencyGate(MAX_CONCURRENT_REQUESTS, {
  maxWaitMs: GATE_MAX_WAIT_MS,
});

// Interceptor de REQUEST
api.interceptors.request.use(
  async (config) => {
    // La sesión cambió en otra pestaña: no se firma nada más. El token de
    // `localStorage` ya no es el de esta pestaña, y enviarlo significaría
    // actuar como otra persona. Ver `utils/sessionLock.js`.
    if (isSessionLocked()) {
      return Promise.reject(new SessionChangedError('request-blocked'));
    }
    if (!isGateExemptRequest(config)) {
      await requestGate.acquire();
      config._hasConcurrencySlot = true;
    }
    try {
      const token = localStorage.getItem('token');
      const tenantId = localStorage.getItem('tenant_id');

      // Respeta headers explícitos (por ejemplo, login agrega X-Tenant-ID sin token)
      const hasAuthHeader = Boolean(config.headers?.Authorization);
      const hasTenantHeader = Boolean(config.headers?.['X-Tenant-ID']);

      if (token && !hasAuthHeader) {
        config.headers.Authorization = `Bearer ${token}`;
      }
      
      // Solo enviamos el tenant automático cuando hay sesión activa; evita golpear DB de tenant en landing
      if (token && tenantId && !hasTenantHeader) {
        config.headers['X-Tenant-ID'] = tenantId;
      }
    } catch (e) {
      // Ignora si localStorage no está disponible
    }

    return config;
  },
  (error) => Promise.reject(error)
);

// Interceptor de RESPONSE (útil para ver errores sin abrir cada request)
api.interceptors.response.use(
  (response) => {
    if (response?.config?._hasConcurrencySlot) {
      response.config._hasConcurrencySlot = false;
      requestGate.release();
    }
    return response;
  },
  (error) => {
    if (error?.config?._hasConcurrencySlot) {
      error.config._hasConcurrencySlot = false;
      requestGate.release();
    }
    const status = error?.response?.status;
    const data = error?.response?.data;
    const url = error?.config
      ? (error.config.baseURL || '') + (error.config.url || '')
      : '<<no-config>>';

    if (process.env.NODE_ENV === 'development' || FORCE_API_LOGS) {
      const detail = error?.response
        ? (data && typeof data === 'object' ? JSON.stringify(data) : data)
        : (error?.code || error?.message || 'no response (backend down or network error)');
      console.warn(
        'API Error ->',
        status ?? '—',
        url,
        detail
      );
    }

    // Attach user-friendly message for UI (Phase 4.2: 403, 503, timeout)
    try {
      error.userMessage = getApiErrorMessage(error);
    } catch (_) {
      error.userMessage = 'Something went wrong. Please try again.';
    }

    // Handle 401 Unauthorized (R3). During bootstrap grace, only AuthContext /users/me may revoke JWT.
    if (
      shouldRedirectToLoginOn401(
        status,
        url,
        window.location.pathname,
        window.location.search,
      )
    ) {
      if (shouldSuppress401Logout()) {
        return Promise.reject(error);
      }
      if (process.env.NODE_ENV === 'development' || FORCE_API_LOGS) {
        console.warn('[API] Token invalid/expired - force logout', url);
      }
      dispatchForceLogout({ status, url });
    }

    return Promise.reject(error);
  }
);

// --- NEW ONBOARDING API FUNCTIONS ---

/**
 * Creates a new project. Can handle both legacy signature and new object-based signature.
 * @param {string|Object} projectNameOrData - Either project name (legacy) or project data object (new).
 * @param {string} clientName - The name of the new client (legacy signature only).
 * @param {string} country - The country (legacy signature only).
 * @param {string} crewNumber - The crew number (legacy signature only).
 * @returns {Promise<object>} - The newly created project object.
 */
export const createProject = async (projectNameOrData, clientName, country, crewNumber) => {
  try {
    // Check if we're using the new object-based signature
    if (typeof projectNameOrData === 'object' && projectNameOrData !== null) {
      // New signature: createProject(projectData)
      const projectData = projectNameOrData;
      const response = await api.post('/projects', projectData);
      return response.data;
    } else {
      // Legacy signature: createProject(projectName, clientName, country, crewNumber)
      const projectName = projectNameOrData;
      
      // STEP 1: Create the client first to get a client_id.
      const clientResponse = await api.post('/clients', { client_name: clientName });
      const clientId = clientResponse.data.client_id;

      // STEP 2: Now, create the project using the new client_id and other required fields.
      const projectResponse = await api.post('/projects', {
        project_name: projectName,
        client_id: clientId,
        country: country,
        crew_number: crewNumber
      });

      return projectResponse.data;
    }
  } catch (error) {
    console.error('Error during project creation flow:', error.response?.data || error.message);
    
    // Handle specific HTTP status codes
    if (error.response?.status === 409) {
      // Conflict - duplicate project name
      const errorMsg = error.response?.data?.detail || 'A project with this name already exists.';
      throw new Error(errorMsg);
    } else if (error.response?.status === 404) {
      // Not found - client doesn't exist
      throw new Error('The selected client was not found. Please refresh and try again.');
    } else if (error.config && error.config.url && error.config.url.includes('/clients')) {
      throw new Error('Failed to create the new client.');
    } else if (error.response?.data?.detail) {
      // Use backend error message if available
      throw new Error(error.response.data.detail);
    } else {
      throw new Error('Failed to create the project. Please try again.');
    }
  }
};

/**
 * Updates an existing project with its type (2D/3D) and service phases.
 * @param {number} projectId - The ID of the project to update.
 * @param {string} projectType - The type of the project ('2D' or '3D').
 * @param {Array<string>} phases - An array of strings representing the selected service phases.
 * @returns {Promise<object>} - The updated project object.
 */
export const updateProjectConfiguration = async (projectId, projectType, phases) => {
  try {
    // Using PATCH as we are partially updating the project resource.
    const response = await api.patch(`/projects/${projectId}/configure`, {
      project_type: projectType,
      phases: phases,
    });
    return response.data;
  } catch (error) {
    console.error('Error updating project configuration:', error.response?.data || error.message);
    throw error;
  }
};

/**
 * Fetches all projects from the API.
 * @returns {Promise<Array>} - Array of project objects.
 */
export const getProjects = async () => {
  try {
    const response = await api.get('/projects');
    return response.data;
  } catch (error) {
    const status = error?.response?.status;
    const detail = error?.response?.data?.detail ?? error?.response?.data;
    console.error(
      'Error fetching projects:',
      status != null ? `HTTP ${status}` : error?.message || error?.code || 'network error',
      detail || error?.response?.data || ''
    );
    throw error;
  }
};

/**
 * Fetches a specific project by ID from the API.
 * @param {number} projectId - The ID of the project to fetch.
 * @returns {Promise<object>} - The project object.
 */
export const getProject = async (projectId) => {
  try {
    const response = await api.get(`/projects/${projectId}`);
    return response.data;
  } catch (error) {
    console.error(`Error fetching project ${projectId}:`, error.response?.data || error.message);
    throw error;
  }
};

/**
 * Fetches all clients from the API.
 * @returns {Promise<Array>} - Array of client objects.
 */
export const getClients = async () => {
  try {
    const response = await api.get('/clients');
    return response.data;
  } catch (error) {
    console.error('Error fetching clients:', error.response?.data || error.message);
    throw error;
  }
};

/**
 * Creates a new client.
 * @param {Object} clientData - The client data object.
 * @returns {Promise<object>} - The newly created client object.
 */
export const createClient = async (clientData) => {
  try {
    const response = await api.post('/clients', clientData);
    return response.data;
  } catch (error) {
    console.error('Error creating client:', error.response?.data || error.message);
    throw error;
  }
};

// --- PROJECT SCOPE API FUNCTIONS ---

/**
 * Fetches the detailed technical scope for a specific project.
 * @param {number} projectId - The ID of the project.
 * @returns {Promise<object>} - The project scope data containing survey, drilling, recording configurations.
 */
export const getProjectScope = async (projectId) => {
  try {
    const response = await api.get(`/projects/${projectId}/scope`);
    return response.data;
  } catch (error) {
    console.error(`Error fetching scope for project ${projectId}:`, error.response ? error.response.data : error.message);
    // Return a default structure on error to prevent crashes
    return { survey: null, drilling: null, recording: null };
  }
};

/**
 * Saves the detailed technical scope for a specific project.
 * @param {number} projectId - The ID of the project.
 * @param {object} scopeData - The scope data object containing survey, drilling, recording configurations.
 * @returns {Promise<object>} - The updated project scope data.
 */
export const saveProjectScope = async (projectId, scopeData) => {
  try {
    const response = await api.patch(`/projects/${projectId}/scope`, scopeData);
    return response.data;
  } catch (error) {
    console.error(`Error saving scope for project ${projectId}:`, error.response ? error.response.data : error.message);
    throw error.response ? error.response.data : new Error('Network or other error');
  }
};

// --- PROJECT DRAFT BUDGET API FUNCTIONS ---

/**
 * Fetches or creates the draft budget for a specific project.
 * @param {number} projectId - The ID of the project.
 * @returns {Promise<object>} - The project's draft budget with full details.
 */
export const getProjectDraftBudget = async (projectId) => {
  try {
    const response = await api.get(`/projects/${projectId}/draft-budget`);
    return response.data;
  } catch (error) {
    console.error(`Error fetching draft budget for project ${projectId}:`, error.response ? error.response.data : error.message);
    throw error.response ? error.response.data : new Error('Network or other error');
  }
};

/**
 * Updates an existing budget with new data.
 * @param {number} budgetId - The ID of the budget to update.
 * @param {object} budgetData - The complete budget data object.
 * @returns {Promise<object>} - The updated budget data.
 */
export const updateBudget = async (budgetId, budgetData) => {
  try {
    const response = await api.put(`/budgets/${budgetId}`, budgetData);
    return response.data;
  } catch (error) {
    console.error(`Error updating budget ${budgetId}:`, error.response ? error.response.data : error.message);
    throw error.response ? error.response.data : new Error('Network or other error');
  }
};


// --- EXISTING EXPORTS ---
// The new functions should be placed before these lines.

// Export nombrado (getApiErrorMessage for UI error display - Phase 4.2)
export { api, getApiErrorMessage };
// Export default
export default api;
/**
 * Baja un fichero de `uploads/` AL DISCO, por el camino que funciona entre
 * orígenes distintos.
 *
 * ───────────────────────────────────────────────────────────────────────────
 * POR QUÉ NO BASTA `<a href={getUploadUrl(...)} download="...">`
 * ───────────────────────────────────────────────────────────────────────────
 *
 * El atributo `download` de HTML **se ignora en una navegación de origen
 * cruzado**, y el SPA y el backend son orígenes distintos —`:3000` contra
 * `:8000` en local, dominios distintos en producción—. Sin él, el navegador
 * hace lo que le dicta la respuesta; y la puerta genérica (`StaticFiles`) **no
 * manda `Content-Disposition`**, así que un PDF se abre en una pestaña en vez
 * de guardarse. Ése era el síntoma de CDR Archive, y el mismo de NPT Report
 * Archive, que nadie había mirado porque su carpeta está vacía en producción.
 *
 * Lo que sí funciona, y es el patrón que ya usaban Print PDF, los adjuntos de
 * licitación, los PDF de kits y las copias de seguridad: traerse los bytes con
 * `fetch`, envolverlos en un `blob:` —que es **del mismo origen que el SPA**— y
 * pinchar un ancla con `download`. Ahí el atributo sí manda.
 *
 * El `fetch` cruzado necesita CORS, cosa que un `<img>` no necesita. Medido
 * contra el backend: `/uploads/…` responde `access-control-allow-origin` con el
 * origen del SPA, y el preflight también. Y como la credencial viaja en el `?t=`
 * que añade `getUploadUrl`, no hace falta ninguna cabecera propia: es una
 * petición simple y no dispara preflight.
 *
 * @param {string} rutaOUrl  ruta de `uploads/` o URL ya construida
 * @param {string} nombre    el nombre con el que se guarda; si falta, el del path
 * @returns {Promise<void>}  lanza si la descarga falla, para que el llamante avise
 */
export const descargarDesdeUploads = async (rutaOUrl, nombre) => {
  const url = getUploadUrl(rutaOUrl);
  if (!url) throw new Error('No hay fichero que descargar.');

  const respuesta = await fetch(url, { credentials: 'omit' });
  if (!respuesta.ok) {
    // La puerta contesta 404 a propósito también cuando deniega, así que aquí
    // no se puede distinguir «no está» de «no puedes». El mensaje lo dice.
    throw new Error(`No se pudo descargar el fichero (${respuesta.status}).`);
  }

  const blob = await respuesta.blob();
  const blobUrl = window.URL.createObjectURL(blob);
  try {
    const enlace = document.createElement('a');
    enlace.href = blobUrl;
    // Un nombre legible, no el UUID del disco. Si no viene, el último segmento
    // de la ruta, sin el `?t=` del ticket.
    enlace.download = nombre || url.split('?')[0].split('/').pop() || 'descarga';
    document.body.appendChild(enlace);
    enlace.click();
    document.body.removeChild(enlace);
  } finally {
    window.URL.revokeObjectURL(blobUrl);
  }
};
